import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import Avatar from "@shared/v2/Avatar";
import Button from "@shared/v2/Button";
import IconButton from "@shared/v2/IconButton";
import Toggle from "@shared/v2/Toggle";
import { Popover } from "@shared/v2/Popover";
import {
  EllipsisSolidV6,
  GripDotsVerticalSolidV6,
  PencilSolidV6,
  StarRegularV6,
  StarSolidV6,
  UserSolidV6,
} from "@shared/v2/Icomoon";
import Tooltip from "@shared/v2/Tooltip";
import { updateContact } from "../../reducers/websiteReducer/websiteReducer";

const AgentLenderCard = ({
  agent: propsAgent,
  className,
  popoverOptions,
  lender,
  onEdit,
  onStar,
  starDisabled,
  disabled,
}) => {
  const dispatch = useDispatch();
  const [agent, setAgent] = useState(propsAgent);

  useEffect(() => {
    setAgent(propsAgent);
  }, [propsAgent]);

  return (
    <div
      className={`tw-bg-white tw-border-[1px] tw-border-solid tw-border-gray-10 tw-rounded-[4px] tw-p-[16px] tw-flex tw-flex-col tw-gap-[16px] ${className}`}
    >
      <div className="tw-flex tw-justify-between tw-items-start">
        {agent ? (
          <IconButton
            onClick={onStar}
            disabled={starDisabled}
            size="small"
            schema={agent.isFeatured ? "starred-yellow" : "tertiary"}
          >
            {agent.isFeatured ? <StarSolidV6 /> : <StarRegularV6 />}
          </IconButton>
        ) : (
          <span className="tw-min-w-[24px]" />
        )}
        <Avatar
          src={agent?.avatarUrl || lender?.avatarUrl}
          alt={agent?.fullName || lender?.fullName}
          size="large"
          className={agent && !agent.showOnWebsiteRoster ? "tw-opacity-40" : ""}
        />
        {!disabled && agent ? (
          <GripDotsVerticalSolidV6 size="l" className="tw-ml-[8px]" />
        ) : (
          <span className="tw-min-w-[24px]" />
        )}
      </div>
      <div
        className={`tw-flex tw-flex-col tw-items-center ${agent && agent.showOnWebsiteRoster !== "true" ? "tw-opacity-40" : ""}`}
      >
        <div className="tw-flex tw-gap-[8px] tw-items-center tw-text-14d tw-font-bold tw-overflow-hidden tw-max-w-full">
          <div className="tw-flex tw-flex-1 tw-whitespace-nowrap tw-overflow-hidden">
            <span className="tw-overflow-hidden tw-text-ellipsis">{agent?.fullName || lender?.fullName}</span>
          </div>
          {agent?.userUuid && (
            <Tooltip
              trigger={<UserSolidV6 />}
              content="This person is a seated user in your CRM"
              innerClassName="tw-max-w-[180px]"
            />
          )}
        </div>
        <div className="tw-text-14d tw-font-normal tw-text-gray-50">
          {agent?.jobTitle || agent?.title || lender?.jobTitle || <>&nbsp;</>}
        </div>
      </div>
      <div className="tw-min-h-[42px] tw-self-center tw-flex tw-flex-col tw-gap-[10px]">
        {agent && (
          <>
            <Toggle
              size="xsmall"
              labelClassName="tw-m-0 tw-font-semibold"
              text="Display on roster"
              checked={agent.showOnWebsiteRoster === "true"}
              onChange={({ target: { checked } }) => {
                setAgent((a) => ({ ...a, showOnWebsiteRoster: String(checked) }));
                dispatch(
                  updateContact({
                    id: agent.id,
                    userUuid: agent.userUuid,
                    showOnWebsiteRoster: String(checked),
                  }),
                );
              }}
            />
            <Toggle
              size="xsmall"
              labelClassName="tw-m-0 tw-font-semibold"
              text="Profile page"
              checked={agent.showAgentProfilePage === "true"}
              onChange={({ target: { checked } }) => {
                setAgent((a) => ({ ...a, showAgentProfilePage: String(checked) }));
                dispatch(
                  updateContact({
                    id: agent.id,
                    userUuid: agent.userUuid,
                    showAgentProfilePage: String(checked),
                  }),
                );
              }}
            />
          </>
        )}
      </div>
      <div className="tw-flex tw-gap-[8px]">
        {popoverOptions && (
          <Popover placement="bottom-end">
            <Popover.Trigger>
              <IconButton size="small" schema="tertiary">
                <EllipsisSolidV6 />
              </IconButton>
            </Popover.Trigger>
            <Popover.Content>{popoverOptions}</Popover.Content>
          </Popover>
        )}
        <Button
          schema="secondary"
          className="tw-flex-1 tw-flex tw-gap-[4px] tw-justify-center tw-items-center"
          onClick={onEdit}
        >
          Edit <PencilSolidV6 size="s" />
        </Button>
      </div>
    </div>
  );
};

AgentLenderCard.propTypes = {
  agent: PropTypes.shape(),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  lender: PropTypes.shape(),
  onEdit: PropTypes.func.isRequired,
  popoverOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onStar: PropTypes.func,
  starDisabled: PropTypes.bool,
};

AgentLenderCard.defaultProps = {
  agent: null,
  className: "",
  disabled: false,
  lender: null,
  onStar: () => {},
  starDisabled: false,
};

const SortableAgentLenderCard = ({ id, agent, disabled, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    outline: isDragging ? "none" : undefined,
    cursor: disabled ? "default" : "grab",
    zIndex: isDragging ? 50 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="tw-max-w-[300px]">
      {isDragging ? (
        <div className="tw-h-full tw-w-[4px] tw-bg-theme-primary tw-rounded-[4px]" />
      ) : (
        <AgentLenderCard
          disabled={disabled}
          agent={agent}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      )}
    </div>
  );
};

SortableAgentLenderCard.displayName = "SortableAgentLenderCard";
SortableAgentLenderCard.propTypes = AgentLenderCard.propTypes;

export default AgentLenderCard;
export { SortableAgentLenderCard };
